/**
 * `queryClient` — the single, stable TanStack Query client instance for the
 * Frontend.
 *
 * ─── Protected extensibility seam (Req 16.7) ────────────────────────────────
 * This module is one of the four protected foundation modules called out in
 * the design (§Extensibility Hooks). Along with `api/client.ts`,
 * `providers/AppProviders.tsx`, and `router/routes.tsx`, it forms the fixed
 * surface every feature composes against.
 *
 *   Feature authors MUST NOT edit this file.
 *
 * Concretely:
 *   - Do NOT add feature-specific `staleTime` / `gcTime` overrides here.
 *     Pass per-query options at the `useQuery` call site instead.
 *   - Do NOT add a persister, offline adapter, or devtools wiring here.
 *     Compose a wrapper provider (for example `PersistedQueryProvider`) in
 *     `providers/AppProviders.tsx` — it receives this same `queryClient` as a
 *     prop and layers behaviour around it without mutating the base config
 *     (design.md §Extensibility Hooks table row "Additional caching layer").
 *   - Do NOT branch on `import.meta.env.MODE` to change defaults here.
 *     Environment-specific behaviour belongs in the composition layer.
 *
 * The reason this file is protected is that TanStack Query defaults leak into
 * every cache entry the app produces. If features were free to tune them, we
 * would lose the ability to reason globally about cache freshness, retry
 * counts, and error routing (see §Timeouts and retry mapping).
 *
 * Requirements satisfied:
 *   - Req 5.1: TanStack Query is the sole Query_Cache for REST_API data.
 *   - Req 5.2: Cached data is served immediately and revalidated in the
 *     background according to a configured stale time.
 *   - Req 16.7: The `QueryClient` configuration is one of the documented
 *     protected foundation modules; features extend it by composing new
 *     providers, not by editing it.
 *
 * See:
 *   - design.md §State Management Design → QueryClient configuration
 *     (authoritative source for every value below).
 *   - design.md §Timeouts and retry mapping (explains how the retry policy
 *     interacts with `ApiError.status` and the Axios 10s timeout).
 */

import { QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/errors';

/**
 * The single, stable TanStack Query client instance consumed by
 * `providers/AppProviders.tsx` and, transitively, every `useQuery` hook in
 * the app (Req 5.1).
 *
 * Values below mirror design.md §State Management Design → QueryClient
 * configuration. Do not tweak them per-feature — see the header docblock.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * 5 minutes. Content is edit-refreshed on the CMS side, so a query is
       * considered fresh for 5 minutes before TanStack Query treats it as
       * stale and triggers a background revalidation on the next mount
       * (Req 5.2, and design.md §QueryClient configuration).
       */
      staleTime: 5 * 60 * 1000,

      /**
       * 30 minutes. How long an inactive query result is kept in cache
       * before it is garbage-collected. Kept generous so that navigating
       * Home → Blog → Home does not re-fetch the home page's data
       * (design.md §QueryClient configuration).
       */
      gcTime: 30 * 60 * 1000,

      /**
       * Retry policy — the single source of truth for how the Frontend
       * reacts to API failures (Req 5.4, 17.1, 17.2, 17.3).
       *
       * Mapping (design.md §Timeouts and retry mapping):
       *   - 404 responses            → NOT retried; caller renders NotFound.
       *   - Other 4xx (400–499)      → NOT retried; caller renders Error.
       *   - 5xx responses            → retried up to 2 more times (3 total).
       *   - Network / timeout errors → `ApiError.status === 0`, falls into
       *                                the default branch and is retried.
       *
       * `error` is typed as `Error` by TanStack Query. Because `ApiError`
       * extends `Error`, `instanceof ApiError` narrows correctly under
       * `strict: true`.
       */
      retry: (failureCount: number, error: Error): boolean => {
        if (error instanceof ApiError) {
          // Req 17.2: 404 short-circuits to the NotFound page — retrying
          // would just delay the inevitable and waste a request budget.
          if (error.status === 404) {
            return false;
          }
          // Req 17.3: any other 4xx is a client-side / auth / validation
          // problem the CMS will keep rejecting. Surface it to Error
          // component immediately instead of retrying.
          if (error.status >= 400 && error.status < 500) {
            return false;
          }
        }
        // 5xx, transport failures (ApiError.status === 0 per Req 17.1),
        // and non-ApiError rejections all fall through here: up to 3
        // attempts total (initial + 2 retries), matching design.md
        // §QueryClient configuration.
        return failureCount < 2;
      },

      /**
       * Exponential backoff capped at 8s: 2s → 4s → 8s across the two
       * retries above. The cap keeps the tab responsive if the CMS is
       * flapping (design.md §QueryClient configuration).
       */
      retryDelay: (attempt: number): number =>
        Math.min(1000 * 2 ** attempt, 8000),

      /**
       * `'online'` — pause queries while the browser reports offline and
       * resume them when connectivity returns. Combined with
       * `refetchOnReconnect: true` below, this gives users a seamless
       * "everything catches up when I'm back online" experience without
       * bespoke offline plumbing here (that lives in a future
       * `PersistedQueryProvider`, per design.md §Extensibility Hooks).
       */
      networkMode: 'online',

      /**
       * `false` — do not refetch every query when the tab regains focus.
       * The site is content-heavy but not real-time; refetching on focus
       * would waste bandwidth and could visibly re-render pages while a
       * reader is mid-article. `staleTime` above handles freshness on
       * navigation (design.md §QueryClient configuration).
       */
      refetchOnWindowFocus: false,

      /**
       * `true` — after a network drop, refresh any active queries so the
       * user sees current content instead of a snapshot taken before the
       * outage (design.md §QueryClient configuration).
       */
      refetchOnReconnect: true,
    },
    mutations: {
      /**
       * `0` — mutations are user-initiated (forms, newsletter signup,
       * comment submission when those features land). Silently retrying
       * them risks duplicate side effects (e.g. duplicate signups). The
       * calling component is responsible for offering an explicit retry
       * affordance instead (design.md §QueryClient configuration).
       */
      retry: 0,
    },
  },
});

/**
 * Public type alias for consumers that need to type a prop or context
 * value carrying the shared client — for example a future
 * `PersistedQueryProvider` that wraps and forwards the same instance.
 *
 * Exporting this keeps callers from re-typing `QueryClient` and preserves
 * the "one client only" invariant at the type level (Req 16.7).
 */
export type AppQueryClient = typeof queryClient;
